import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaCheckCircle,
  FaCopy,
  FaEdit,
  FaLayerGroup,
  FaPlus,
  FaSave,
  FaSearch,
  FaShieldAlt,
  FaWallet,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { hasAccess, readStoredAdminUser } from './adminAccessControl';
import { ACCOUNTING_FORM_TEMPLATES, findAccountingTemplateByKey, getCentralAccountMap } from './accountingSetupConfig';
import { buildAccountingReportCenterPath } from './reportCenterLinks';
import {
  buildApiErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  getInitialScopedOutlets,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './AccountingSetup.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const POS_API_BASE_URL = process.env.REACT_APP_API_PRODUCT_URL || '';

const isSuccessResponse = value => value === 0 || value === '0';

const CATEGORY_OPTIONS = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

function PosAccountMappings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = hasAccess('/pos-account-mappings', currentUser);
  const canOpenAnalyticsCenter = hasAccess('/report-center', currentUser);
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryOutletId = queryParams.get('outlet_id') || '';
  const queryTemplate = queryParams.get('template') || '';
  const queryAccountId = queryParams.get('account_id') || '';
  const initialOutlets = getInitialScopedOutlets(currentUser);
  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [accounts, setAccounts] = useState([]);
  const [centralAccounts, setCentralAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState('');
  const [queryPresetHandled, setQueryPresetHandled] = useState(false);
  const [copySourceOutlet, setCopySourceOutlet] = useState('');
  const [copyMode, setCopyMode] = useState('fill_missing');
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    category: 'Asset',
    transactionType: '',
    purpose: '',
    isActive: true,
  });

  const selectedOutletRecord = useMemo(
    () => outlets.find(outlet => String(outlet.id) === String(selectedOutlet)) || null,
    [outlets, selectedOutlet]
  );
  const copySourceOptions = useMemo(
    () => outlets.filter(outlet => String(outlet.id) !== String(selectedOutlet)),
    [outlets, selectedOutlet]
  );

  const fetchCentralAccounts = useCallback(async token => {
    const response = await fetch(`${API_BASE_URL}accounting/accounts?limit=500`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok || !isSuccessResponse(data.error)) {
      throw new Error(data.message || 'Failed to fetch central accounts');
    }
    setCentralAccounts(Array.isArray(data.results) ? data.results : []);
  }, []);

  const loadAccounts = useCallback(async (token, outletId) => {
    if (!outletId) {
      return [];
    }

    const response = await fetch(`${POS_API_BASE_URL}api/accounts/?outlet_id=${encodeURIComponent(outletId)}`, {
      headers: buildAuthHeaders(token),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Failed to fetch POS accounts'));
    }

    return Array.isArray(data) ? data : [];
  }, []);

  const fetchAccounts = useCallback(async (token, outletId) => {
    if (!outletId) {
      setAccounts([]);
      return;
    }

    setLoading(true);
    try {
      const records = await loadAccounts(token, outletId);
      setAccounts(records);
    } catch (error) {
      console.error('Failed to fetch POS accounts:', error);
      setAccounts([]);
      Swal.fire('Error', error.message || 'Failed to fetch POS accounts', 'error');
    } finally {
      setLoading(false);
    }
  }, [loadAccounts]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;
    const initialize = async () => {
      const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
      if (!active || !token) {
        return;
      }

      setAuthToken(token);
      try {
        const [scopedOutlets] = await Promise.all([
          fetchScopedOutlets({ token, currentUser }),
          fetchCentralAccounts(token),
        ]);
        if (!active) {
          return;
        }
        setOutlets(scopedOutlets);
        setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
      } catch (error) {
        console.error('Failed to initialize POS account mapping:', error);
        Swal.fire('Error', error.message || 'Failed to initialize POS account mapping', 'error');
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canAccess, currentUser, fetchCentralAccounts, navigate, queryOutletId, verifyAndRefreshToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchAccounts(authToken, selectedOutlet);
  }, [authToken, fetchAccounts, selectedOutlet]);

  useEffect(() => {
    if (copySourceOptions.length === 0) {
      setCopySourceOutlet('');
      return;
    }

    setCopySourceOutlet(prev => {
      if (prev && copySourceOptions.some(outlet => String(outlet.id) === String(prev))) {
        return prev;
      }

      const outletOne = copySourceOptions.find(outlet => String(outlet.id) === '1');
      return outletOne?.id || copySourceOptions[0].id;
    });
  }, [copySourceOptions]);

  useEffect(() => {
    setQueryPresetHandled(false);
  }, [queryTemplate, queryAccountId, selectedOutlet]);

  const centralAccountMap = useMemo(() => getCentralAccountMap(centralAccounts), [centralAccounts]);

  const filteredAccounts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return accounts;
    }

    return accounts.filter(account =>
      [account.id, account.name, account.category, account.transaction_type, account.purpose]
        .some(value => String(value || '').toLowerCase().includes(query))
    );
  }, [accounts, search]);

  const resetForm = () => {
    setEditingId('');
    setFormData({
      id: '',
      name: '',
      category: 'Asset',
      transactionType: '',
      purpose: '',
      isActive: true,
    });
  };

  const handleAccountCodeChange = value => {
    const trimmedValue = value.trim();
    const centralAccount = centralAccountMap.get(trimmedValue);
    setFormData(prev => ({
      ...prev,
      id: trimmedValue,
      name: centralAccount?.AccountName || prev.name,
      category: centralAccount?.AccountType || prev.category,
    }));
  };

  const handleTemplatePick = template => {
    setFormData(prev => ({
      ...prev,
      transactionType: template.transactionType,
      purpose: template.purpose,
      category: prev.id && centralAccountMap.get(prev.id)?.AccountType ? centralAccountMap.get(prev.id).AccountType : template.category,
      name: prev.name || template.label,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: formData.id,
        name: formData.name,
        category: formData.category,
        is_active: formData.isActive,
        outlet_id: Number(selectedOutlet),
        transaction_type: formData.transactionType,
        purpose: formData.purpose,
      };

      const url = editingId
        ? `${POS_API_BASE_URL}api/accounts/${encodeURIComponent(editingId)}`
        : `${POS_API_BASE_URL}api/accounts/`;
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to save account mapping');
      }

      await fetchAccounts(authToken, selectedOutlet);
      resetForm();
      Swal.fire('Success', editingId ? 'Account mapping updated.' : 'Account mapping created.', 'success');
    } catch (error) {
      console.error('Failed to save account mapping:', error);
      Swal.fire('Error', error.message || 'Failed to save account mapping', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = record => {
    setEditingId(String(record.mapping_id || ''));
    setFormData({
      id: record.id,
      name: record.name || '',
      category: record.category || 'Asset',
      transactionType: record.transaction_type || '',
      purpose: record.purpose || '',
      isActive: Boolean(record.is_active),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyMappings = async () => {
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet tujuan lebih dulu.', 'error');
      return;
    }

    if (!copySourceOutlet) {
      Swal.fire('Error', 'Pilih outlet sumber lebih dulu.', 'error');
      return;
    }

    if (String(copySourceOutlet) === String(selectedOutlet)) {
      Swal.fire('Error', 'Outlet sumber dan tujuan tidak boleh sama.', 'error');
      return;
    }

    const sourceOutletName = copySourceOptions.find(outlet => String(outlet.id) === String(copySourceOutlet))?.name || `Outlet ${copySourceOutlet}`;
    const targetOutletName = selectedOutletRecord?.name || `Outlet ${selectedOutlet}`;
    const isReplaceMode = copyMode === 'replace_existing';

    const confirmation = await Swal.fire({
      title: 'Copy account mappings?',
      html: `
        <div style="text-align:left">
          <p style="margin-bottom:8px;"><strong>Sumber:</strong> ${sourceOutletName}</p>
          <p style="margin-bottom:8px;"><strong>Tujuan:</strong> ${targetOutletName}</p>
          <p style="margin-bottom:0;"><strong>Mode:</strong> ${isReplaceMode ? 'Samakan dengan source' : 'Isi yang belum ada'}</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Copy sekarang',
      cancelButtonText: 'Batal',
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setCopying(true);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/accounts/copy-mappings`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          source_outlet_id: Number(copySourceOutlet),
          target_outlet_id: Number(selectedOutlet),
          mode: copyMode,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Failed to copy POS account mappings'));
      }

      await fetchAccounts(authToken, selectedOutlet);

      const result = data?.data || {};
      Swal.fire(
        'Success',
        [
          `${result.source_count || 0} mapping aktif dibaca dari outlet sumber.`,
          `${result.created_count || 0} dibuat baru, ${result.updated_count || 0} diaktifkan ulang, ${result.replaced_count || 0} diganti, ${result.skipped_count || 0} dilewati.`,
        ].join(' '),
        'success'
      );
    } catch (error) {
      console.error('Failed to copy account mappings:', error);
      Swal.fire('Error', error.message || 'Failed to copy account mappings', 'error');
    } finally {
      setCopying(false);
    }
  };

  useEffect(() => {
    if (queryPresetHandled || !selectedOutlet) {
      return;
    }

    if (queryAccountId) {
      const matchedAccount = accounts.find(account => String(account.id) === String(queryAccountId));
      if (matchedAccount) {
        startEdit(matchedAccount);
        setQueryPresetHandled(true);
        return;
      }
    }

    if (queryTemplate) {
      const template = findAccountingTemplateByKey(queryTemplate);
      if (template) {
        setEditingId('');
        setFormData(prev => ({
          ...prev,
          transactionType: template.transactionType,
          purpose: template.purpose,
          category: template.category,
          name: prev.name || template.label,
        }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    setQueryPresetHandled(true);
  }, [accounts, queryAccountId, queryPresetHandled, queryTemplate, selectedOutlet]);

  const columns = [
    {
      name: 'Account',
      sortable: true,
      grow: 1.5,
      cell: row => (
        <div className="table-identity">
          <span className="table-identity__avatar">
            {String(row.id || '').slice(0, 2).toUpperCase() || 'AC'}
          </span>
          <span className="table-identity__meta">
            <strong>{row.id}</strong>
            <small>{row.name}</small>
          </span>
        </div>
      ),
    },
    {
      name: 'Mapping',
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.transaction_type}:{row.purpose}</strong>
          <small>{row.category}</small>
        </span>
      ),
    },
    {
      name: 'Scope',
      width: '160px',
      cell: row => (
        <span className={`status-pill ${Number(row.outlet_id || 0) > 0 ? '' : 'is-warning'}`}>
          {Number(row.outlet_id || 0) > 0 ? `Outlet ${row.outlet_id}` : 'Global'}
        </span>
      ),
    },
    {
      name: 'Ledger',
      width: '180px',
      cell: row => (
        <span className={`status-pill ${centralAccountMap.has(String(row.id || '').trim()) ? '' : 'is-danger'}`}>
          {centralAccountMap.has(String(row.id || '').trim()) ? 'Matched' : 'Missing in ledger'}
        </span>
      ),
    },
    {
      name: 'Status',
      width: '140px',
      cell: row => (
        <span className={`status-pill ${row.is_active ? '' : 'is-muted'}`}>
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      name: 'Actions',
      width: '130px',
      cell: row => (
        <Button variant="outline-warning" size="sm" onClick={() => startEdit(row)}>
          <FaEdit /> Edit
        </Button>
      ),
    },
  ];

  const centralMatch = centralAccountMap.get(formData.id);

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaWallet}
          eyebrow="Commerce accounting"
          title="Account Mapping"
          description="Kelola kode akun POS per outlet dan pastikan setiap mapping memakai account ID yang valid di ledger pusat."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Accounting Control', to: '/accounting-setup' },
            { label: 'Account Mapping' },
          ]}
          meta={(
            <div className="d-flex flex-wrap gap-2">
              <span className="soft-stat">
                <FaLayerGroup /> {filteredAccounts.length} mappings
              </span>
              <span className="soft-stat">
                <FaShieldAlt /> {selectedOutlet || '-'}
              </span>
            </div>
          )}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="primary"
                type="button"
                onClick={resetForm}
              >
                <FaPlus /> Add mapping
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(buildAccountingReportCenterPath('trial-balance', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaCheckCircle /> Open Trial Balance
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate(`/accounting-setup?outlet_id=${selectedOutlet}`)} disabled={!selectedOutlet}>
                <FaWallet /> Back to control
              </Button>
            </div>
          )}
        />

        <div className="accounting-panel-grid">
          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Mapping editor</span>
                <h2>{editingId ? 'Update account mapping' : 'Add new account mapping'}</h2>
                <p>Pilih outlet, ambil kode akun dari ledger pusat, lalu map ke `transaction_type` dan `purpose` POS.</p>
              </div>
              <span className={`status-pill ${centralMatch || !formData.id ? '' : 'is-danger'}`}>
                {centralMatch ? 'Central account matched' : formData.id ? 'Needs valid ledger account' : 'Select account code'}
              </span>
            </div>

            <Form onSubmit={handleSubmit}>
              <div className="form-grid">
                <Form.Group className="field-stack span-6">
                  <Form.Label>Outlet</Form.Label>
                  <Form.Select value={selectedOutlet} onChange={event => setSelectedOutlet(event.target.value)}>
                    <option value="">Select outlet</option>
                    {outlets.map(outlet => (
                      <option key={outlet.id} value={outlet.id}>
                        {outlet.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="field-stack span-6">
                  <Form.Label>Account code</Form.Label>
                  <Form.Control
                    list="central-account-list"
                    value={formData.id}
                    onChange={event => handleAccountCodeChange(event.target.value)}
                    placeholder="Masukkan account_id pusat"
                    disabled={Boolean(editingId)}
                  />
                  <Form.Text className="text-muted">
                    {editingId
                      ? 'Kode akun dikunci setelah mapping dibuat. Buat mapping baru jika perlu mengganti kode akun.'
                      : 'Gunakan `account_id` yang benar-benar ada di ledger pusat apifitness.'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="field-stack span-6">
                  <Form.Label>Account name</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={event => setFormData(prev => ({ ...prev, name: event.target.value }))}
                    placeholder="Nama akun"
                  />
                </Form.Group>

                <Form.Group className="field-stack span-6">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={formData.category}
                    onChange={event => setFormData(prev => ({ ...prev, category: event.target.value }))}
                  >
                    {CATEGORY_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="field-stack span-6">
                  <Form.Label>Transaction type</Form.Label>
                  <Form.Control
                    value={formData.transactionType}
                    onChange={event => setFormData(prev => ({ ...prev, transactionType: event.target.value }))}
                    placeholder="sale / purchase / inventory / expense / income"
                  />
                </Form.Group>

                <Form.Group className="field-stack span-6">
                  <Form.Label>Purpose</Form.Label>
                  <Form.Control
                    value={formData.purpose}
                    onChange={event => setFormData(prev => ({ ...prev, purpose: event.target.value }))}
                    placeholder="sales / cash / inventory / expense"
                  />
                </Form.Group>

                <Form.Group className="field-stack span-6">
                  <Form.Check
                    type="switch"
                    id="mapping-active-switch"
                    label="Mapping active"
                    checked={formData.isActive}
                    onChange={event => setFormData(prev => ({ ...prev, isActive: event.target.checked }))}
                  />
                </Form.Group>

                <div className="field-stack span-6 accounting-action-stack">
                  <Form.Label>&nbsp;</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    <Button type="submit" disabled={saving || !selectedOutlet}>
                      <FaSave /> {saving ? 'Saving...' : editingId ? 'Update mapping' : 'Save mapping'}
                    </Button>
                    <Button variant="outline-secondary" type="button" onClick={resetForm}>
                      <FaPlus /> New mapping
                    </Button>
                  </div>
                </div>
              </div>
            </Form>

            <div className="accounting-template-strip">
              {ACCOUNTING_FORM_TEMPLATES.map(template => (
                <button
                  key={template.key}
                  type="button"
                  className="accounting-template-chip"
                  onClick={() => handleTemplatePick(template)}
                >
                  {template.key}
                </button>
              ))}
            </div>

            <datalist id="central-account-list">
              {centralAccounts.map(account => (
                <option key={account.AccountID} value={account.AccountID}>
                  {account.AccountName}
                </option>
              ))}
            </datalist>
          </section>

          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Outlet shortcut</span>
                <h2>Copy mappings from another outlet</h2>
                <p>Gunakan outlet yang sudah rapi sebagai template. Hanya mapping outlet yang aktif yang dicopy; mapping global tidak ikut dipindahkan.</p>
              </div>
              <span className={`status-pill ${copySourceOutlet && selectedOutlet && String(copySourceOutlet) !== String(selectedOutlet) ? '' : 'is-warning'}`}>
                {selectedOutletRecord ? `Target: ${selectedOutletRecord.name}` : 'Select target outlet'}
              </span>
            </div>

            <div className="form-grid">
              <Form.Group className="field-stack span-6">
                <Form.Label>Source outlet</Form.Label>
                <Form.Select value={copySourceOutlet} onChange={event => setCopySourceOutlet(event.target.value)} disabled={!selectedOutlet}>
                  <option value="">Select source outlet</option>
                  {copySourceOptions.map(outlet => (
                    <option key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>Copy mode</Form.Label>
                <Form.Select value={copyMode} onChange={event => setCopyMode(event.target.value)} disabled={!selectedOutlet}>
                  <option value="fill_missing">Isi yang belum ada</option>
                  <option value="replace_existing">Samakan dengan source</option>
                </Form.Select>
              </Form.Group>

              <div className="field-stack span-6">
                <Form.Label>Target outlet</Form.Label>
                <div className="accounting-inline-note">
                  {selectedOutletRecord
                    ? `${selectedOutletRecord.name} akan menerima hasil copy.`
                    : 'Pilih outlet tujuan dari editor di sebelah kiri.'}
                </div>
              </div>

              <div className="field-stack span-6 accounting-action-stack">
                <Form.Label>&nbsp;</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={handleCopyMappings}
                    disabled={copying || !selectedOutlet || !copySourceOutlet || String(copySourceOutlet) === String(selectedOutlet)}
                  >
                    <FaCopy /> {copying ? 'Copying...' : 'Copy mappings'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="accounting-inline-note">
              Mode `Isi yang belum ada` aman untuk melengkapi outlet baru tanpa menimpa slot mapping yang sudah ada.
            </div>
            <div className="accounting-inline-note">
              Mode `Samakan dengan source` akan mengganti mapping target jika `transaction_type:purpose` yang sama sudah ada.
            </div>
            <div className="accounting-inline-note">
              Minimal mapping inti untuk transaksi standar tetap `sale:sales`, `sale:cash`, `purchase:inventory`, dan `purchase:cash`.
            </div>
            <div className="accounting-inline-note">
              Untuk outlet `Store Bahan Bangunan`, tambahkan juga mapping `sale:receivable` agar metode bayar `Hutang Customer` bisa dipost ke akun piutang customer.
            </div>
            <div className="accounting-inline-note">
              Gunakan kode akun pusat untuk semua mapping POS. Jika outlet memakai QRIS, transfer, diskon, pajak, atau COGS, tambahkan mapping khususnya juga. Mapping fee hanya opsional untuk billing bulanan terpisah.
            </div>
            <div className="accounting-inline-note">
              Untuk stock adjustment dan stock opname, siapkan mapping `inventory:adjustment_loss` dan `inventory:adjustment_gain`. Jika outlet memakai struktur akun berbeda, gunakan fallback seperti `expense:inventory_adjustment`, `expense:stock_loss`, `income:inventory_adjustment`, `income:stock_gain`, atau `other_income:inventory_adjustment`.
            </div>
            <div className="accounting-inline-note">
              Untuk shift closing, siapkan mapping `expense:cash_shortage` dan `income:cash_overage` agar selisih kas otomatis masuk ke Profit & Loss.
            </div>
          </section>
        </div>

        <section className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">{filteredAccounts.length} mappings</span>
            <div className="control-with-icon search-control">
              <span className="control-with-icon__icon">
                <FaSearch />
              </span>
              <Form.Control
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Cari kode akun, nama, transaction type, atau purpose"
              />
            </div>
          </div>
          <AdminDataTable
            columns={columns}
            data={filteredAccounts}
            progressPending={loading}
            pagination
            highlightOnHover
            striped
            dense
            emptyTitle="No POS account mappings"
            emptyDescription="Belum ada mapping akun POS untuk outlet yang dipilih."
          />
        </section>
      </Container>
    </div>
  );
}

export default PosAccountMappings;
